import styled, { css } from 'styled-components';

export type PillVariant = 'default' | 'deleted' | 'muted';

interface BaseProps {
	$variant: PillVariant;
	$clickable: boolean;
}

const variantStyles = ($variant: PillVariant) => {
	if ($variant === 'deleted') {
		return css`
			background-color: ${({ theme }) => theme.red[200]};
			color: ${({ theme }) => theme.red[700]};
			border: 1px solid ${({ theme }) => theme.red[200]};
			font-weight: 400;
			text-decoration: line-through;
			opacity: 0.7;
		`;
	}
	if ($variant === 'muted') {
		return css`
			background-color: ${({ theme }) => theme.gray[200]};
			color: ${({ theme }) => theme.gray[700]};
			border: 1px solid ${({ theme }) => theme.gray[200]};
			font-weight: 400;
		`;
	}
	return css`
		background-color: ${({ theme }) => theme.gray[200]};
		color: ${({ theme }) => theme.gray[800]};
		border: 1px solid ${({ theme }) => theme.gray[200]};
		font-weight: 700;
	`;
};

// Single-element pill (click-only, remove-only, or static)
export const Base = styled.span<BaseProps>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 1px 8px;
	border-radius: 12px;
	font-size: 12px;
	white-space: nowrap;
	transition: all 0.2s ease;
	${({ $variant }) => variantStyles($variant)}

	${({ $clickable, theme }) =>
		$clickable &&
		css`
			appearance: none;
			font-family: inherit;

			&:hover {
				background-color: ${theme.gray[400]};
				border-color: ${theme.gray[400]};
				color: ${theme.gray[1000]};
			}
		`}
`;

export const Label = styled.span<{ $noTruncate?: boolean }>`
	max-width: ${({ $noTruncate }) => ($noTruncate ? 'none' : '140px')};
	overflow: ${({ $noTruncate }) => ($noTruncate ? 'visible' : 'hidden')};
	text-overflow: ${({ $noTruncate }) => ($noTruncate ? 'clip' : 'ellipsis')};
	white-space: nowrap;
`;

// Split-mode wrapper (just layout, no visual styling)
export const SplitWrapper = styled.span`
	display: inline-flex;
	align-items: center;
`;

interface SplitLabelProps {
	$variant: PillVariant;
	$clickable: boolean;
}

// Left side in split mode (label) - button or span, hover only when clickable
export const SplitLabel = styled.span<SplitLabelProps>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 1px 6px 1px 8px;
	border-radius: 12px 0 0 12px;
	font-size: 12px;
	font-family: inherit;
	white-space: nowrap;
	transition: background-color 0.15s ease, color 0.15s ease;
	${({ $variant }) => variantStyles($variant)}
	border-right: none;

	${({ $clickable, theme }) =>
		$clickable &&
		css`
			appearance: none;

			&:hover {
				background-color: ${theme.gray[400]};
				color: ${theme.gray[1000]};
			}
		`}
`;

interface SplitButtonProps {
	$variant: PillVariant;
}

// Right button in split mode (X side)
export const SplitRemove = styled.button<SplitButtonProps>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	padding: 1px 0;
	border-radius: 0 12px 12px 0;
	font-size: 12px;
	appearance: none;
	transition: background-color 0.15s ease, color 0.15s ease;
	${({ $variant }) => variantStyles($variant)}
	border-left: 1px solid ${({ theme }) => theme.gray[400]};

	color: ${({ theme }) => theme.gray[400]};

	height: 17px;
	&:hover {
		background-color: ${({ theme }) => theme.gray[400]};
		color: ${({ theme }) => theme.gray[800]};
	}
`;

export const IconContainer = styled.span`
	margin-left: -2px;
	margin-top: 2px;
`;
